import React from 'react'
import { Modal, Tag, Tooltip, Typography, message } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { renderDisabledReasonInfo } from 'components/goods/GoodsList/utils/getGoodsListColumns'
import { productApi } from 'api/product'
import { ERoles } from 'models/auth'
import { EProductStatus, IFrontVariationProduct } from 'models/product'
import {
  BLOCKED_PRODUCT_HINT,
  ENABLE_PRODUCT_STATUS_KEY,
  getProductStatusAction,
  getVariationStatusItems,
  productTags
} from 'shared/constants/product'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { showServerError } from 'shared/utils/showServerError'
import { appSelectors } from 'store/app'
import { toVariationProduct } from '../utils/variationUtils'

interface IVariationStatusProps {
  product?: IFrontVariationProduct
  parentStatus?: EProductStatus
  onChange: (product: IFrontVariationProduct) => void
}

export const VariationStatus: React.FC<IVariationStatusProps> = ({
  product,
  parentStatus,
  onChange
}) => {
  const role = useAppSelector(appSelectors.selectRole)

  if (!product?.status) {
    return <Typography.Text type='secondary'>—</Typography.Text>
  }

  const { id, name, status } = product

  const applyStatus = async (key: string) => {
    const action = getProductStatusAction(key, id)
    const isEnable = key === ENABLE_PRODUCT_STATUS_KEY
    const isDisable = key === EProductStatus.DISABLED
    if (!action && !isEnable && !isDisable) {
      return
    }
    try {
      message.loading('Обновление статуса')
      if (action) {
        await productApi.setActions(action)
      } else {
        await productApi.toggleProductDisabled(id, isDisable)
      }
      // Ответ действия — только сообщение: статус и поля отключения берём из самого товара
      const response = await productApi.getProduct(id)
      onChange(toVariationProduct(response.data, name))
      message.destroy()
      message.success('Статус вариации изменён')
    } catch (e) {
      message.destroy()
      showServerError(e)
    }
  }

  const onStatusClick = (key: string) => {
    if (key !== EProductStatus.ARCHIVED) {
      applyStatus(key)
      return
    }
    bodyScrollLock.enable()
    Modal.confirm({
      centered: true,
      title: 'Удалить вариацию?',
      content: 'Вариация пропадёт с витрины. Отменить данное действие будет невозможно.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Удалить',
      cancelText: 'Закрыть',
      onOk() {
        return applyStatus(key).finally(() => bodyScrollLock.disable())
      },
      onCancel() {
        bodyScrollLock.disable()
      }
    })
  }

  const items = getVariationStatusItems(
    status,
    role as ERoles,
    parentStatus === EProductStatus.PUBLISHED
  )
  const { label, color } = productTags[status]
  const statusTag = <Tag color={color}>{label}</Tag>

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {items.length ? (
        <StatusDropdown
          items={items}
          label={label}
          color={color}
          onClick={(e) => onStatusClick(e.key)}
        />
      ) : status === EProductStatus.BLOCKED ? (
        <Tooltip title={BLOCKED_PRODUCT_HINT}>{statusTag}</Tooltip>
      ) : (
        statusTag
      )}
      {renderDisabledReasonInfo(product, role === ERoles.ADMIN)}
    </div>
  )
}
